/**
 * RBAC roles, personas, permissions, and module-access maps for AstraConnect.
 *
 * Design decisions (see plan docs/superpowers/plans/2026-06-17-role-persona-rbac.md):
 * - Persona is the PRIMARY access gate. Permissions are SECONDARY refinements
 *   within a persona's already-granted surface.
 * - AC-ADM-3 ("changing a persona definition requires no code deploy") is
 *   SATISFIED: the runtime gate is server-driven `canSeeModule` (CurrentUserContext),
 *   which reads `raw.currentPersona.modules[]` from the backend. PERSONA_MODULES is
 *   NO LONGER a runtime input — it survives only as a TEST-ONLY parity fixture (the
 *   canonical PRD baseline the DB grants are checked against). See its docstring.
 * - Role + persona code VALUES below are CONFIRMED against the connect-gql source
 *   of truth (AppRoles + member_360 seed, migration 001): role codes lowercase
 *   ("admin"), persona codes uppercase ("PROVIDER"…). The care-mgmt #35173 proxy
 *   returns these verbatim. Remaining TBD: PERMISSIONS value strings (below).
 */

// `import type` only — erased at compile time, so this introduces no runtime
// circular dependency with featureCatalog.ts (which imports PERSONA_MODULES from
// here). Typing the seed values as ModuleId[] makes catalog/seed drift a tsc error.
import type { ModuleId } from "./featureCatalog";

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

/**
 * Known role code strings returned by `connectCurrentUser.role.code` (AcRole).
 * CONFIRMED against connect-gql: AppRoles (`src/domain/constants/AppRoles.ts`) +
 * the `member_360.roles` seed (migration 001) → role codes are LOWERCASE
 * ("admin"/"clinical"/"readonly"); RequireAppRole compares `r.code === "admin"`.
 */
export const ROLES = {
  ADMIN: "admin", // connect-gql AppRoles.ADMIN = "admin" (lowercase); roles seed = ('admin','Admin')
} as const;

export type RoleValue = (typeof ROLES)[keyof typeof ROLES];

/**
 * The full set of role codes the backend accepts (lowercase), CONFIRMED against
 * connect-gql AppRoles + the `member_360.roles` seed (migration 001):
 * admin / clinical / readonly. `ROLES` above only promotes ADMIN to a named
 * constant (it's the only one the FE gates on), but bulk-import client-side
 * validation must accept all three so legitimate clinical/readonly rows are not
 * rejected before submit. Extend this set if the backend roles seed grows.
 */
export const KNOWN_ROLE_CODES: ReadonlySet<string> = new Set([
  "admin",
  "clinical",
  "readonly",
]);

// ---------------------------------------------------------------------------
// Personas — PRD-authoritative (AstraConnect PRD §4, 2026-06-18)
// ---------------------------------------------------------------------------

/**
 * The AstraConnect personas per PRD §4.
 * CONFIRMED against connect-gql `member_360.personas` seed (migration 001:70-72) —
 * codes are UPPERCASE and match these constants verbatim, so `connectCurrentUser`
 * persona codes pass through BACKEND_PERSONA_MAP 1:1 (identity map below).
 *
 * NOTE: the "ADMIN" persona is DELIBERATELY ABSENT here. AstraConnect admin is
 * governed solely by the `admin` ROLE (see ROLES.ADMIN). Note the split (ACT-1303):
 * member-facing module VISIBILITY follows the active persona with NO admin bypass
 * (CurrentUserContext's canSeeModule), while the per-module ACTIONS layer
 * (useFeatureAccess `resolveModuleActions`) still grants the admin role all-access.
 * The old ADMIN super-persona was a duplicate admin signal; it is now vestigial —
 * the member_360.personas seed row may still
 * exist in the DB, but the FE neither offers it nor grants access from it. Do NOT
 * re-add "ADMIN" here: it would resurrect the second, persona-based admin gate.
 */
export const PERSONAS = {
  PROVIDER: "PROVIDER",                     // member_360.personas seed
  CUSTOMER_SERVICE: "CUSTOMER_SERVICE",     // member_360.personas seed
  MSO: "MSO",                               // member_360.personas seed
  CARE_MANAGER: "CARE_MANAGER",             // member_360.personas seed
  MEDICAL_ASSISTANT: "MEDICAL_ASSISTANT",   // member_360.personas seed
} as const;

export type PersonaValue = (typeof PERSONAS)[keyof typeof PERSONAS];

/**
 * Persona codes the FE must NOT surface in any persona picker/filter, even if the
 * backend `listPersonas` still returns them. "ADMIN" lives here: the member_360
 * seed row may persist, but the ADMIN persona is vestigial — admin access is the
 * ROLE's job. Filtering it out of option lists stops admins from being told to
 * assign a persona that no longer grants anything. Case-insensitive compare.
 */
export const HIDDEN_PERSONA_CODES: ReadonlySet<string> = new Set(["ADMIN"]);

/** True if `code` is a persona the FE should hide from pickers/filters. */
export function isHiddenPersona(code: string | null | undefined): boolean {
  return !!code && HIDDEN_PERSONA_CODES.has(code.toUpperCase());
}

// ---------------------------------------------------------------------------
// ADAPTER — Backend persona string → FE PERSONAS enum key
// ---------------------------------------------------------------------------
/**
 * Maps the raw code string returned by `connectCurrentUser.currentPersona.code` /
 * `personas[].code` to the canonical FE PersonaValue.
 *
 * CONFIRMED: persona codes from connect-gql member_360.personas seed (migration 001)
 * are UPPERCASE and match the PERSONAS constants verbatim, so this is an identity map.
 * If the backend ever changes its code strings, update the LEFT-hand side (keys) here
 * — the right-hand PERSONAS values are the stable FE identifiers.
 *
 * SECURITY NOTE: normalizePersona() returns undefined for any code NOT present here.
 * CurrentUserContext treats an unrecognized code as fail-closed (keeps the raw string
 * so its server-driven canSeeModule closure denies all modules — no PERSONA_MODULES
 * key matches) — NOT as the allow-all null seam. Do not add a catch-all entry or
 * default that maps unknown codes to a known persona.
 */
export const BACKEND_PERSONA_MAP: Record<string, PersonaValue> = {
  [PERSONAS.PROVIDER]:           PERSONAS.PROVIDER,
  [PERSONAS.CUSTOMER_SERVICE]:   PERSONAS.CUSTOMER_SERVICE,
  [PERSONAS.MSO]:                PERSONAS.MSO,
  [PERSONAS.CARE_MANAGER]:       PERSONAS.CARE_MANAGER,
  [PERSONAS.MEDICAL_ASSISTANT]:  PERSONAS.MEDICAL_ASSISTANT,
  // "ADMIN" intentionally omitted — normalizePersona("ADMIN") now returns
  // undefined, so a stale backend ADMIN-persona code fails closed (canSeeModule
  // denies) rather than unlocking everything. Admin access comes from the ROLE.
};

/**
 * Normalize a backend persona string to the canonical FE PersonaValue.
 * Returns undefined if the string is unrecognized.
 */
export function normalizePersona(backendValue: string | null | undefined): PersonaValue | undefined {
  if (!backendValue) return undefined;
  return BACKEND_PERSONA_MAP[backendValue];
}

// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------

/**
 * Known `value` strings from `connectCurrentUser.permissions[].value` (AcPermission).
 * These are secondary gates within a persona's already-allowed surface.
 * TODO: confirm exact permission value strings with connect-gql schema (care-mgmt #35173).
 */
export const PERMISSIONS = {
  VIEW_CLAIMS:           "view_claims",           // TODO: confirm backend string
  VIEW_MEDICATIONS:      "view_medications",      // TODO: confirm backend string
  ADMIN_USER_MANAGEMENT: "admin_user_management", // TODO: confirm backend string
  // Extend as additional permission.value keys are confirmed.
} as const;

export type PermissionValue = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ---------------------------------------------------------------------------
// PERSONA_MODULES — INTERIM hardcoded map (Phase 1 seed only)
// ---------------------------------------------------------------------------
/**
 * Maps each persona to the moduleId strings it can access.
 * Source: AstraConnect PRD §4 (2026-06-18, unchanged as of 2026-06-23).
 *
 * ⚠️  NON-RUNTIME PARITY FIXTURE (ACT-1319) — this map is NOT consulted at
 * runtime. The runtime module-visibility gate is the SERVER-DRIVEN `canSeeModule`
 * closure in `src/auth/CurrentUserContext.tsx`, which reads the backend's
 * per-persona `raw.currentPersona.modules[]`. AC-ADM-3 is satisfied by that
 * server-driven path (persona definitions are admin-editable via
 * `AcUpsertPersonaModule` — no code deploy).
 *
 * This constant is retained ONLY as:
 *   1. the compile-time module-id vocabulary anchor (typed `ModuleId[]`, so a
 *      typo'd/removed module id fails `tsc`), and
 *   2. the canonical PRD baseline for the DB-parity check at rollout — the
 *      `member_360.persona_modules` active grants are expected to match this map
 *      byte-for-byte (see roles.test.ts). Update it only when the PRD persona→tab
 *      mapping itself changes, and re-verify DB parity per the ACT-1319 rollout.
 *
 * Surfaces annotated "new upstream dependency" do not yet exist in
 * @astrana/ui — they are included here so the baseline is complete, but the tabs
 * will not render until upstream modules land (see plan §9).
 */
export const PERSONA_MODULES: Record<string, readonly ModuleId[]> = {
  [PERSONAS.PROVIDER]: [
    "Home",           // summary, quality gaps, HCC
    "Medications",
    "AmbientAI",      // new upstream dependency — see plan §9
    "Translator",     // AC-TRX-1 live interpreter rail
    "WriteBack",      // new upstream dependency — see plan §9
    "Auths",
    // G1 (2026-08-19/20) — the chart's visit notes (read + draft entry point).
    // Clinical content; PROVIDER is the primary consumer. See featureCatalog.ts's
    // Notes entry and the CUSTOMER_SERVICE persona below (deliberately omitted).
    "Notes",
    // P6 host wiring (patient-priority-index) — provider's ranked patient
    // worklist. NOT in the original AstraConnect PRD §4 table (post-dates
    // it); added here as the seed's compile-time vocabulary anchor. Real
    // enforcement still requires the backend's `member_360.persona_modules`
    // grant to include this id (see featureCatalog.ts's ship-gate note).
    "PriorityWorklist",
    // P4 host wiring (ADT Hospital/ED notification bell) — provider-facing,
    // same seed placement as PriorityWorklist. Real enforcement still
    // requires the backend's member_360.persona_modules grant to include
    // this id (see featureCatalog.ts's ship-gate note).
    "AdtNotifications",
  ],
  [PERSONAS.CUSTOMER_SERVICE]: [
    "MemberSearch",
    "Eligibility",
    "Claims",
    "CareTeam",
    "Outreach",       // new upstream dependency — see plan §9
    // "Notes" deliberately OMITTED (G1, 2026-08-19/20): customer-service should
    // not see clinical visit-note content. See featureCatalog.ts's Notes entry.
  ],
  [PERSONAS.MSO]: [
    "NetworkSearch",  // new upstream dependency — see plan §9
    "Home",           // quality gaps + HCC subset
    "Claims",
    "Auths",
    "Outreach",       // new upstream dependency — see plan §9
  ],
  [PERSONAS.CARE_MANAGER]: [
    // CM-8319 — Next Best Action card + its Activity history tab. Seeded to
    // CARE_MANAGER only: the NBA engine's playbooks are care-management
    // workflows (TOC follow-up, AWV, gap closure) addressed to a care team.
    // As with every id here this seed is a TEST-ONLY parity fixture — real
    // enforcement needs the backend's member_360.persona_modules grant, so
    // widening to other personas is a DB change, not a code change.
    "HomeNextBestAction",
    "Activity",
    "Home",           // quality gaps subset
    "CareTeam",
    "Eligibility",
    "Auths",
    "Outreach",       // new upstream dependency — see plan §9
  ],
  [PERSONAS.MEDICAL_ASSISTANT]: [
    "Eligibility",
    "Documents",
    "PracticeSchedule", // admin-fed; new upstream dependency — see plan §9
    "Home",             // quality gaps subset
    "Translator",       // AC-TRX-1 live interpreter rail
  ],
} as const;

// ---------------------------------------------------------------------------
// UNGATED_MODULES — REMOVED (AC-ADM-3, 2026-08-21)
// ---------------------------------------------------------------------------
// This file used to export `UNGATED_MODULES = new Set(["Diagnoses", "Labs"])` —
// the interim "always visible, not in the PRD persona vocabulary" escape hatch,
// with a TODO to make those tabs server-driven in Phase 2 (AC-ADM-3). This IS
// Phase 2: `Diagnoses` and `Labs` are now real FEATURE_CATALOG tab entries and
// real `member_360.persona_modules` rows (migration 0027), so they flow through
// the same server-driven persona gate + admin-settable tab order as every other
// tab. The constant had no remaining runtime reader when it was deleted (the
// visibility decision moved to CurrentUserContext's `canSeeModule` in ACT-1303),
// so removing it is a pure cleanup. Do NOT reintroduce it: an ungated tab is
// invisible to the admin console's per-persona tab reordering.

// ---------------------------------------------------------------------------
// MODULE_PERMISSIONS — secondary permission gates within a persona's surface
// ---------------------------------------------------------------------------
/**
 * Maps moduleId → the permission.value required to access it (on top of persona gate).
 * Absent/null = no secondary permission gate; persona membership alone is sufficient.
 * TODO (§8 Q3): confirm which modules require a secondary permission and exact values.
 *
 * Phase 2 (AC-ADM-3): secondary permission gating deferred — values unconfirmed by
 * Pathways. MODULE_PERMISSIONS is exported for Phase 2 wiring but canSeeModule does
 * NOT consult it yet; enforcing unconfirmed strings would risk a new regression.
 */
export const MODULE_PERMISSIONS: Record<string, string | null> = {
  Claims:      PERMISSIONS.VIEW_CLAIMS,
  Medications: PERMISSIONS.VIEW_MEDICATIONS,
  // null/absent = visible to any user whose persona includes the module
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// NOTE (ACT-1303): the member-facing module-visibility gate is NOT here anymore.
// It moved to the SERVER-DRIVEN `canSeeModule` closure in
// `src/auth/CurrentUserContext.tsx`, which reads the backend's per-persona
// `raw.currentPersona.modules[]` list (and fails closed on a hard query error /
// omitted module). The old compile-time helper that lived here — a
// PERSONA_MODULES lookup with an `admin` ROLE all-access bypass — contradicted
// the new no-admin-bypass model and was dead in this package (only its own test
// referenced it), so it was removed. `PERSONA_MODULES` above is now a NON-RUNTIME
// parity fixture (the featureCatalog identity check + the DB-parity test baseline),
// NOT the runtime visibility gate. Do NOT reintroduce a role-based all-access
// helper here: admin visibility follows the active persona (see CurrentUserContext),
// with only the scoped no-real-persona admin bypass added in ACT-1319.

/**
 * Returns true if `permissionValues` (a Set of permission.value strings) contains
 * `value`. Used as the secondary gate inside a persona's allowed surface.
 */
export function hasPermission(
  permissionValues: Set<string>,
  value: string,
): boolean {
  return permissionValues.has(value);
}
